import React, { useContext, useEffect, useRef } from 'react';
import { DropzoneArea } from 'material-ui-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { resetDocState, setDocToastHide, setDocumentUploadStatus, stageDocuments } from './document-upload-slice';
import { DocumentUploadState } from './document-upload-models';
import { TcsmStates } from '../../../../root/tcsm-model';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { FormTypes } from '../../models/form-models';
import { TcsmSiteSettingsProps } from '../../../../global/models/epi-model';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { docUploadStyles } from '../../../../global/constants/theme/styles/components/doc-upload';
import { Toast } from '../../../../global/components/messages/toast';
import { DocUploadToast } from './doc-upload-toast';
import { ToastCombo } from '../../../../global/components/messages/toast-combo';

interface DocumentUploadProps {
	formType: FormTypes,
	defaultShow?: boolean,
	hide?: boolean,
	requiredDocUpload?: boolean,
}

export function DocumentUpload(props: DocumentUploadProps) {
	const classes = docUploadStyles();
	const app = useContext(DemographicsContext);
	const documentUpoloadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);
	const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state.epiProps?.siteSettingsState?.siteSettings);
	const dispatch = useDispatch();
	const setting = siteSettings?.documentUploadSettings?.filter(a => a.formType?.toLocaleUpperCase() === props.formType?.toUpperCase())[0];
	const docUploadRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		dispatch(resetDocState(app));
	}, [app]);

	let uploadDescription = setting?.instructionDescription;

	if (documentUpoloadState?.showDemographicWarning && documentUpoloadState?.showInternationalWarning){
        uploadDescription = setting?.demographicInternationalWarningDescription || 'Documentation is required for an international address change and for a demographic change';
    }    

    if (!documentUpoloadState?.showDemographicWarning && documentUpoloadState?.showInternationalWarning){
        uploadDescription = setting?.internationalWarningDescription || 'Documentation is required for an international address change';
    }

	if ((!documentUpoloadState?.showDocumentUpload && !props.defaultShow) || props.hide) {
		return null;
	}

	const handler = (loadedFiles: File[]) => {
		if (loadedFiles && loadedFiles.length > 0) {
			dispatch(stageDocuments(loadedFiles, app));
			dispatch(setDocumentUploadStatus(true, app, loadedFiles.length));
		}
	};

	const handlePreviewIcon = (fileObject) => {
		const { name } = fileObject.file;

		return <a href={fileObject?.data} download={name}>{name}</a>;
	};

	return (
		<DividerSection topDivider >
			<ToastCombo
				renderEmbeddedMessage={documentUpoloadState?.showDocumentUpload || props.defaultShow}
				toast={
					<Toast
						message={<DocUploadToast searchResultsRef={docUploadRef}
							showInternationalWarning={documentUpoloadState?.showInternationalWarning}
							showDemographicWarning={documentUpoloadState?.showDemographicWarning} />}
						hide={documentUpoloadState?.hideToast}
						onClose={() => dispatch(setDocToastHide(true, app))} />
				}
				hideToast={props.defaultShow}
				embeddedMessage={
					<div className={classes.docUploadContainer} ref={docUploadRef}>
						<ContentSpacer>
							<h2>{setting?.instructionTitle}{(setting?.required || props.requiredDocUpload) && <span className='required-indicator'>*</span>}</h2>
						</ContentSpacer>						
						<ContentSpacer>
							<div className='rich-text'>
								<div dangerouslySetInnerHTML={{ __html: uploadDescription }} />
							</div>
						</ContentSpacer>

						<DropzoneArea
							onChange={handler}
							maxFileSize={Number(setting?.fileSize) || 3000000}
							filesLimit={Number(setting?.maxFileNumber) || 3}
							acceptedFiles={setting?.fileTypes?.split(',') || []}
							getPreviewIcon={handlePreviewIcon}
							previewText={siteSettings?.docUploadChipTitle}
							dropzoneText={setting?.dropzoneText}
							showPreviews
							showPreviewsInDropzone={false}
							useChipsForPreview
							previewGridProps={{ container: { spacing: 2, wrap: 'wrap' }, item: { xs: 'auto' } }}
						/>
					</div>
				}
				messageRef={docUploadRef}
			/>


		</DividerSection>


	);

}

DocumentUpload.defaultProps = {
	defaultShow: false,
	hide: false,
	requiredDocUpload: false,
};